export interface ModuleTutorial {
  id: string;
  moduleNumber: string;
  title: string;
  category: string;
  iconName: string;
  operations: string[];
  advantages: string[];
  clinicalOutputs: string[];
  regulatoryImpact: string[];
  videoDuration: string;
  demoVideoSteps: { step: number; title: string; description: string }[];
}

export const MODULE_TUTORIALS: ModuleTutorial[] = [
  {
    id: 'aac',
    moduleNumber: '01',
    title: 'AAC Speech Synthesizer & Eye-Gaze Board',
    category: 'Assistive Communication',
    iconName: 'Volume2',
    videoDuration: '3m 45s',
    operations: [
      'Select active student profile at the top selector bar to automatically load their custom vocabulary.',
      'Tap or eye-gaze over high-contrast core vocabulary symbol cards (e.g., "I WANT", "DRINK", "HELP").',
      'Review sentence formulation in the top text bar and click "Speak Phrase" for instant Web Speech TTS audio synthesis.',
      'Add custom vocabulary words with custom audio pitch, rate, and category tags.'
    ],
    advantages: [
      'Gives non-verbal autistic and cerebral palsy students an instant artificial voice.',
      'Eye-gaze dwell timing (200ms-800ms) for students with severe motor impairments.',
      'Multi-lingual speech synthesis supporting English, Hindi, and Marathi pronunciation.'
    ],
    clinicalOutputs: [
      'Instant Web Speech API audio output.',
      'Timestamped AAC Phrase Selection History Log (CSV/PDF exportable).',
      'Core Vocabulary Frequency Analytics Chart.'
    ],
    regulatoryImpact: [
      'RPwD Act 2016 Section 16 (Equal opportunity in communication).',
      'Sugamya Bharat Abhiyan WCAG 2.1 AA Screen Reader Compliance.',
      'NEP 2020 Chapter 6 Inclusive Special Education Technology.'
    ],
    demoVideoSteps: [
      { step: 1, title: 'Select Student Profile', description: 'Choose Master Rahul Sharma from active student bar.' },
      { step: 2, title: 'Build Sentence', description: 'Tap "I WANT" + "WATER" on the high-contrast symbol grid.' },
      { step: 3, title: 'Synthesize Speech', description: 'Click Speak to trigger clear audio output.' }
    ]
  },
  {
    id: 'speech-therapy',
    moduleNumber: '02',
    title: 'Speech & Articulation Therapy Suite',
    category: 'Clinical Therapy',
    iconName: 'Mic',
    videoDuration: '4m 10s',
    operations: [
      'Select target phoneme drill (/r/, /s/, /l/, /k/) from the phonetic dropdown.',
      'Record student articulation audio using Web Audio API microphone recorder.',
      'Compare student audio pitch and formant frequencies against speech pathologist target waveforms.',
      'Log precision accuracy percentage and award token stars.'
    ],
    advantages: [
      'Visual biofeedback showing real-time audio waveforms and pitch accuracy graphs.',
      'Reduces dysarthria and speech sound disorders through gamified repetition.'
    ],
    clinicalOutputs: [
      'Phonetic Articulation Precision Score (0-100%).',
      'Target Audio vs Student Audio Spectrogram Overlay.',
      'CPT Code 92507 Speech Therapy Billing Record.'
    ],
    regulatoryImpact: [
      'RCI (Rehabilitation Council of India) Speech-Language Pathology SOPs.',
      'IDEA 2004 34 CFR § 300.320 Speech-Language Goals.'
    ],
    demoVideoSteps: [
      { step: 1, title: 'Pick Phoneme', description: 'Select /r/ initial sound blend drill.' },
      { step: 2, title: 'Record Audio', description: 'Record student saying "Rabbit".' },
      { step: 3, title: 'Waveform Analysis', description: 'Compare pitch graph against SLP target.' }
    ]
  },
  {
    id: 'autism',
    moduleNumber: '03',
    title: 'Autism ABA Behavior & Token Economy',
    category: 'Behavioral Health',
    iconName: 'Brain',
    videoDuration: '3m 30s',
    operations: [
      'Set target positive behaviors (e.g., "Eye Contact", "Turn-Taking", "Sitting Quietly").',
      'Award 1 to 5 Token Stars instantly when target behavior is observed.',
      'Log Antecedent-Behavior-Consequence (ABC) data entries.',
      'Trigger sensory reward animation when token board reaches 5 stars.'
    ],
    advantages: [
      'Applies evidence-based Applied Behavior Analysis (ABA) token reinforcement principles.',
      'Visual progress bar reduces anxiety and meltdowns in autistic students.'
    ],
    clinicalOutputs: [
      'ABC Behavioral Log Export.',
      'Token Economy Reward Audit Trail.',
      'Behavior Frequency Reduction Graph.'
    ],
    regulatoryImpact: [
      'RPwD Act 2016 Autism Spectrum Benchmark Guidelines.',
      'BACB (Behavior Analyst Certification Board) Practice SOPs.'
    ],
    demoVideoSteps: [
      { step: 1, title: 'Define Task', description: 'Set target: "Sitting during 10-min circle time".' },
      { step: 2, title: 'Award Token Star', description: 'Click +1 Star on visual token board.' },
      { step: 3, title: 'Celebrate Mastery', description: 'Trigger star shower reward upon 5 stars.' }
    ]
  },
  {
    id: 'sign-language',
    moduleNumber: '04',
    title: 'Sign Language & ISL Gestures',
    category: 'Visual / Hearing',
    iconName: 'Eye',
    videoDuration: '4m 00s',
    operations: [
      'Search ISL (Indian Sign Language) dictionary for specific words or phrases.',
      'Play HD video animation demonstrating hand gestures, facial expressions, and fingerspelling.',
      'Test student recognition using interactive sign language flashcard quiz.',
      'Adjust playback speed (0.5x, 0.75x, 1.0x) for learning accessibility.'
    ],
    advantages: [
      'Standardized Indian Sign Language (ISL) video dictionary aligned with ISLRTC guidelines.',
      'Bridges communication between deaf students, teachers, and hearing parents.'
    ],
    clinicalOutputs: [
      'ISL Sign Recognition Mastery Score (e.g., 48/50 Signs Mastered).',
      'Fingerspelling Accuracy Matrix.'
    ],
    regulatoryImpact: [
      'NEP 2020 Indian Sign Language Standardization Guidelines.',
      'RPwD Act 2016 Hearing Impairment Benchmark Rights.'
    ],
    demoVideoSteps: [
      { step: 1, title: 'Search ISL Sign', description: 'Search "WELCOME" in ISL video database.' },
      { step: 2, title: 'Play Video', description: 'Watch slow-motion gesture demonstration.' },
      { step: 3, title: 'Quiz Mastery', description: 'Student selects correct sign in quiz.' }
    ]
  },
  {
    id: 'braille',
    moduleNumber: '05',
    title: 'Braille & Tactile Learning Transcriber',
    category: 'Visual Impairment',
    iconName: 'Compass',
    videoDuration: '3m 50s',
    operations: [
      'Type standard English/Hindi text into the input box to instantly convert into 6-dot Braille cells.',
      'Click 6-dot cell buttons (Dots 1-6) to practice manual Braille cell formulation.',
      'Switch between Grade 1 (Uncontracted) and Grade 2 (Nemeth Math Contracted) Braille modes.',
      'Listen to audio speech synthesis hints for visually impaired students.'
    ],
    advantages: [
      'Enables visually impaired students and sighted teachers to translate text to Braille in real time.',
      'Supports Nemeth Code for secondary mathematics transcription.'
    ],
    clinicalOutputs: [
      'Grade 1 & Grade 2 Braille Unicode Output.',
      'Braille Reading Speed Tracker (Words Per Minute WPM).'
    ],
    regulatoryImpact: [
      'RPwD Act 2016 Visual Impairment & Blindness Assistive Rights.',
      'Sugamya Bharat Abhiyan Accessible Reading Standard.'
    ],
    demoVideoSteps: [
      { step: 1, title: 'Enter Text', description: 'Type "Math Equation: 2 + 2 = 4".' },
      { step: 2, title: 'Convert Braille', description: 'View Nemeth Braille 6-dot cell output.' },
      { step: 3, title: 'Audio Hint', description: 'Hear synthesized audio description of cell.' }
    ]
  },
  {
    id: 'student',
    moduleNumber: '06',
    title: 'Student IEP Progress & UDID Compliance',
    category: 'Special Education',
    iconName: 'GraduationCap',
    videoDuration: '5m 00s',
    operations: [
      'Select student to view official 18-digit UDID card verification details.',
      'Select disability category from RPwD Act 2016 21-disability taxonomy.',
      'Track quarterly SMART IEP goals across Q1, Q2, Q3, and Q4 milestones.',
      'Generate printable PDF Individualized Education Program (IEP) document.'
    ],
    advantages: [
      'Ensures 100% compliance with Government of India UDID disability card tracking.',
      'Automates SMART IEP goal tracking and progress monitoring.'
    ],
    clinicalOutputs: [
      'Printable PDF IEP Summary Report with UDID Verification Stamp.',
      'Quarterly Milestone Progress Meter (0-100%).'
    ],
    regulatoryImpact: [
      'Govt of India DEPwD UDID System Integration.',
      'RPwD Act 2016 21 Disability Category Classification.',
      'IDEA 2004 34 CFR § 300.320 SMART IEP Standard.'
    ],
    demoVideoSteps: [
      { step: 1, title: 'Verify UDID', description: 'Inspect 18-digit UDID card MH-27-00000-2026-1234567.' },
      { step: 2, title: 'Update IEP Goal', description: 'Mark Q3 Goal as "Mastered (92%)".' },
      { step: 3, title: 'Export PDF', description: 'Click Download Official IEP Report.' }
    ]
  },
  {
    id: 'therapist',
    moduleNumber: '07',
    title: 'Therapist Clinical SOAP Notes Suite',
    category: 'Clinical Operations',
    iconName: 'Stethoscope',
    videoDuration: '4m 20s',
    operations: [
      'Enter Subjective patient feedback, Objective clinical metrics, Assessment findings, and Plan for next session.',
      'Select CPT Billing Codes (e.g. CPT 92507 Speech, CPT 97110 Physical Therapy).',
      'Sign digitally with therapist electronic signature stamp.',
      'Lock session note to prevent unauthorized modification.'
    ],
    advantages: [
      'Standardizes clinical documentation according to international allied health practice standards.',
      'Automates CPT code selection for medical insurance and NDIS reimbursement.'
    ],
    clinicalOutputs: [
      'Signed Clinical SOAP Note PDF.',
      'CPT Billing Code Line Item Record.'
    ],
    regulatoryImpact: [
      'HIPAA / GDPR Clinical Documentation Compliance.',
      'RCI Allied Health Practitioner Documentation Standards.'
    ],
    demoVideoSteps: [
      { step: 1, title: 'Write SOAP Note', description: 'Enter S, O, A, P notes for therapy session.' },
      { step: 2, title: 'Assign CPT Code', description: 'Select CPT 92507 Speech Pathology.' },
      { step: 3, title: 'Sign & Lock', description: 'Apply electronic signature and lock record.' }
    ]
  },
  {
    id: 'music-therapy',
    moduleNumber: '22',
    title: 'Music Therapy & Raga Auditory Stimulation',
    category: 'Neuro-Rehab',
    iconName: 'Music',
    videoDuration: '4m 45s',
    operations: [
      'Select Indian Classical Raga scale (*Raga Bhupali*, *Raga Yaman*, *Raga Bairagi*).',
      'Play pentatonic marimba synth keys for sensory soothing and auditory motor entrainment.',
      'Adjust Rhythmic Auditory Stimulation (RAS) metronome tempo from 40 to 160 BPM for gait training.',
      'Toggle 10Hz Alpha Focus Binaural Brainwave soundscapes for classroom attention.'
    ],
    advantages: [
      'Combines traditional Indian Classical Raga therapy with modern neuro-auditory stimulation.',
      'Improves gait motor rhythm and calms sensory meltdowns in autistic students.'
    ],
    clinicalOutputs: [
      'Auditory Stimulation Session Log.',
      'Binaural Alpha Wave Frequency Telemetry.'
    ],
    regulatoryImpact: [
      'WFMT (World Federation of Music Therapy) Clinical Practice Guidelines.',
      'RPwD Act 2016 Neuro-Rehabilitation Accommodations.'
    ],
    demoVideoSteps: [
      { step: 1, title: 'Select Raga Scale', description: 'Choose Raga Bhupali (Pentatonic Soothing).' },
      { step: 2, title: 'Set Metronome', description: 'Set RAS Metronome to 72 BPM for gait walk.' },
      { step: 3, title: 'Start Soundscape', description: 'Activate 10Hz Alpha Binaural Focus Waves.' }
    ]
  }
];
